import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, Plus, Search, Calendar, Target, FileCheck, Clock, Play, Pause, Trash2, Edit2, ChevronRight, AlertTriangle, CheckCircle, XCircle, RefreshCw, MapPin, Users, BarChart3, Upload, Image, Briefcase, Phone, Mail, User, FileSignature, Loader2, Sparkles, Brain, Network, Server, Monitor, Router, HardDrive, Printer, Laptop, Smartphone, Wifi, Shield, Globe, Lock, ExternalLink, Crown, Send, Key, Eye, EyeOff, Download } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast, translateBackendError } from '../../utils/toastUtils';
import { workspaces as workspacesApi, schedules as schedulesApi, scans as scansApi, businessRegistry, vpn } from '../../api/client';
import { downloadInstaller } from '../../api/agents';
import { usePageTitle } from '../../hooks/usePageTitle';
import WorkspaceWizard from '../../components/wizards/WorkspaceWizard';
import { ClientsWelcomeBanner } from '../../components/WelcomeBanner';
import ScheduleCalendar from '../../components/ScheduleCalendar';
import PlanRestrictedButton from '../../components/PlanRestrictedButton';
import { usePlanRestrictions } from '../../hooks/usePlanRestrictions';
import { useI18n } from '../../i18n';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

const WorkspaceManager = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasFeature, getQuotaStatus, isFreePlan, currentPlan } = usePlanRestrictions();
  const { t } = useI18n();
  usePageTitle(t('workspaces.title'));

  // Helper function to translate frequency values
  const getFrequencyLabel = (frequency) => {
    const labels = {
      daily: t('workspaces.frequencyDaily'),
      weekly: t('workspaces.frequencyWeekly'),
      biweekly: t('workspaces.frequencyBiweekly'),
      monthly: t('workspaces.frequencyMonthly'),
      quarterly: t('workspaces.frequencyQuarterly'),
    };
    return labels[frequency] || frequency;
  };

  // Helper function to translate scan type values
  const getScanTypeLabel = (scanType) => {
    const labels = {
      quick: t('workspaces.scanTypeQuick'),
      full: t('workspaces.scanTypeFull'),
      vulnerability: t('workspaces.scanTypeVulnerability'),
      port_scan: t('workspaces.scanTypePortScan'),
      web_scan: t('workspaces.scanTypeWebScan'),
    };
    return labels[scanType] || scanType;
  };
  const translateScanType = (scanType) => {
    if (!scanType) return '-';
    const key = `scans.types.${scanType}`;
    const translated = t(key);
    if (translated !== key) return translated;
    const osintKey = `osint.scanTypes.${scanType}`;
    const osintTranslated = t(osintKey);
    if (osintTranslated !== osintKey) return osintTranslated;
    return scanType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // State
  const [workspacesList, setWorkspacesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showScheduleWizard, setShowScheduleWizard] = useState(false);
  const [scheduleWizardDate, setScheduleWizardDate] = useState(null);
  const [workspaceStats, setWorkspaceStats] = useState(null);
  const [workspaceScans, setWorkspaceScans] = useState([]);
  const [workspaceSchedules, setWorkspaceSchedules] = useState([]);
  const [availableScans, setAvailableScans] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [networkHosts, setNetworkHosts] = useState([]);
  const [networkHostsLoading, setNetworkHostsLoading] = useState(false);

  // Fetch workspaces
  const fetchWorkspaces = useCallback(async () => {
    try {
      setLoading(true);
      const data = await workspacesApi.list(0, 100, searchTerm || null);
      setWorkspacesList(data || []);
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Fetch workspace details
  const fetchWorkspaceDetails = useCallback(async (workspaceId) => {
    try {
      // Use Promise.allSettled to handle partial failures gracefully (schedules API may return 404)
      const results = await Promise.allSettled([
        workspacesApi.getStats(workspaceId),
        workspacesApi.getScans(workspaceId),
        schedulesApi.list(workspaceId).catch(() => []) // Catch 404 errors for schedules
      ]);

      const stats = results[0].status === 'fulfilled' ? results[0].value : null;
      const scansData = results[1].status === 'fulfilled' ? results[1].value : { scans: [] };
      const schedulesData = results[2].status === 'fulfilled' ? results[2].value : [];

      if (stats) setWorkspaceStats(stats);
      setWorkspaceScans(Array.isArray(scansData?.scans) ? scansData.scans : []);
      setWorkspaceSchedules(Array.isArray(schedulesData) ? schedulesData : []);
    } catch (error) {
      console.error('Error fetching workspace details:', error);
    }
  }, []);

  // Fetch available scans for association
  const fetchAvailableScans = useCallback(async () => {
    try {
      const data = await scansApi.list(0, 100);
      setAvailableScans(data.scans || []);
    } catch (error) {
      console.error('Error fetching scans:', error);
    }
  }, []);

  // Fetch AI summary for workspace
  const fetchAiSummary = useCallback(async (workspaceId, forceRefresh = false) => {
    try {
      setAiSummaryLoading(true);
      const data = await workspacesApi.getAiSummary(workspaceId, forceRefresh);
      setAiSummary(data);
    } catch (error) {
      console.error('Error fetching AI summary:', error);
      setAiSummary(null);
    } finally {
      setAiSummaryLoading(false);
    }
  }, []);

  // Fetch network hosts from workspace scans
  const fetchNetworkHosts = useCallback(async (workspaceId) => {
    try {
      setNetworkHostsLoading(true);
      // Get all hosts from workspace scans
      const scansData = await workspacesApi.getScans(workspaceId, 0, 100);
      const scans = scansData.scans || [];

      // Collect hosts from all completed scans
      let allHosts = [];
      for (const scan of scans.filter(s => s.status === 'completed')) {
        try {
          const hosts = await scansApi.getHosts(scan.id);
          if (hosts && hosts.length > 0) {
            allHosts = [...allHosts, ...hosts];
          }
        } catch (e) {
          console.warn(`Could not fetch hosts for scan ${scan.id}`);
        }
      }

      // Remove duplicates by IP
      const uniqueHosts = allHosts.reduce((acc, host) => {
        const ip = host.ip_address;
        if (!acc.find(h => h.ip_address === ip)) {
          acc.push(host);
        }
        return acc;
      }, []);

      setNetworkHosts(uniqueHosts);
    } catch (error) {
      console.error('Error fetching network hosts:', error);
      setNetworkHosts([]);
    } finally {
      setNetworkHostsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaces();
    fetchAvailableScans();
  }, [fetchWorkspaces, fetchAvailableScans]);

  // Auto-select first workspace when list is loaded and none is selected
  useEffect(() => {
    if (workspacesList.length > 0 && !selectedWorkspace && !loading) {
      handleSelectWorkspace(workspacesList[0]);
    }
  }, [workspacesList, selectedWorkspace, loading]);

  useEffect(() => {
    if (selectedWorkspace) {
      fetchWorkspaceDetails(selectedWorkspace.id);
      fetchNetworkHosts(selectedWorkspace.id);
      // Auto-fetch AI summary when workspace is selected
      fetchAiSummary(selectedWorkspace.id, false);
    }
  }, [selectedWorkspace, fetchWorkspaceDetails, fetchNetworkHosts, fetchAiSummary]);

  // Handle edit query parameter from URL (when redirected from workspace details)
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && workspacesList.length > 0 && !showEditModal) {
      const workspaceToEdit = workspacesList.find(ws => ws.id === editId);
      if (workspaceToEdit) {
        // Fetch full workspace data before opening edit modal
        const fetchFullWorkspace = async () => {
          try {
            const fullWorkspace = await workspacesApi.get(editId);
            setSelectedWorkspace(fullWorkspace);
            setShowEditModal(true);
          } catch (error) {
            console.error('Error fetching workspace for edit:', error);
            // Fallback to list data if fetch fails
            setSelectedWorkspace(workspaceToEdit);
            setShowEditModal(true);
          }
        };
        fetchFullWorkspace();
        // Clear the edit param from URL
        searchParams.delete('edit');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, workspacesList, showEditModal]);

  // Handle action=create query parameter from URL (when redirected from /workspaces/create)
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create' && !showCreateWizard) {
      setShowCreateWizard(true);
      // Clear the action param from URL
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, showCreateWizard]);

  // Handle workspace selection - fetch fresh data
  const handleSelectWorkspace = async (workspace) => {
    // Reset states when changing workspace
    setAiSummary(null);
    setWorkspaceScans([]);
    setNetworkHosts([]);
    try {
      // Fetch fresh workspace data
      const freshWorkspace = await workspacesApi.get(workspace.id);
      setSelectedWorkspace(freshWorkspace);
    } catch (error) {
      console.error('Error fetching workspace details:', error);
      // Fallback to list data if fetch fails
      setSelectedWorkspace(workspace);
    }
  };

  // Handle delete workspace
  const handleDeleteWorkspace = async (workspaceId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce workspace ? Cette action est irréversible.')) return;

    try {
      await workspacesApi.delete(workspaceId);
      toast.success(t('workspaces.deleteWorkspace'));
      setSelectedWorkspace(null);
      fetchWorkspaces();
    } catch (error) {
      console.error('Delete workspace error:', error);
      toast.error(t('workspaces.deleteWorkspaceError', 'Erreur lors de la suppression de l\'espace'));
    }
  };

  // Handle edit workspace - ensure full data is loaded
  const handleEditWorkspace = async () => {
    if (!selectedWorkspace) return;
    try {
      // Fetch full workspace data to ensure all fields are available
      const fullWorkspace = await workspacesApi.get(selectedWorkspace.id);
      setSelectedWorkspace(fullWorkspace);
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching workspace for edit:', error);
      // Fallback to current data
      setShowEditModal(true);
    }
  };

  // Handle schedule pause/resume
  const handleToggleSchedule = async (schedule) => {
    try {
      if (schedule.is_paused) {
        await schedulesApi.resume(schedule.id);
        toast.success(t('common.save'));
      } else {
        await schedulesApi.pause(schedule.id);
        toast.success(t('common.save'));
      }
      fetchWorkspaceDetails(selectedWorkspace.id);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Handle run schedule now
  const handleRunScheduleNow = async (scheduleId) => {
    try {
      await schedulesApi.runNow(scheduleId);
      toast.success(t('common.save'));
      fetchWorkspaceDetails(selectedWorkspace.id);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Handle associate scans
  const handleAssociateScans = async (scanIds) => {
    if (!selectedWorkspace) return;

    try {
      await workspacesApi.associateScans(selectedWorkspace.id, scanIds);
      toast.success(t('common.save'));
      fetchWorkspaceDetails(selectedWorkspace.id);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Filtered workspaces
  const filteredWorkspaces = workspacesList.filter(ws =>
    ws.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ws.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ws.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Network stats calculation
  const networkStats = useMemo(() => {
    if (!networkHosts || networkHosts.length === 0) {
      return { total: 0, vulnerable: 0, subnets: 0, services: 0 };
    }

    const vulnerable = networkHosts.filter(h => (h.vulnerability_count || 0) > 0).length;
    const subnets = new Set(networkHosts.map(h => {
      const ip = h.ip_address || '';
      const parts = ip.split('.');
      return parts.length >= 3 ? `${parts[0]}.${parts[1]}.${parts[2]}.0/24` : ip;
    })).size;
    const services = networkHosts.reduce((acc, h) => acc + (h.open_ports?.length || h.services_count || 0), 0);

    return { total: networkHosts.length, vulnerable, subnets, services };
  }, [networkHosts]);

  // Helper function to determine device type
  const getDeviceType = (host) => {
    const hostname = String(host.hostname || '').toLowerCase();
    const services = Array.isArray(host.services) ? host.services.map(s => String(s).toLowerCase()) : [];
    const ports = Array.isArray(host.open_ports) ? host.open_ports : [];

    if (hostname.includes('router') || hostname.includes('gateway') || ports.includes(179)) return 'router';
    if (hostname.includes('switch') || hostname.includes('sw-')) return 'switch';
    if (hostname.includes('firewall') || hostname.includes('fw-') || hostname.includes('pfsense')) return 'firewall';
    if (hostname.includes('printer') || hostname.includes('print') || ports.includes(9100) || ports.includes(515)) return 'printer';
    if (hostname.includes('nas') || hostname.includes('synology') || hostname.includes('qnap') || ports.includes(5000) || ports.includes(5001)) return 'nas';
    if (hostname.includes('phone') || hostname.includes('voip') || ports.includes(5060) || ports.includes(5061)) return 'phone';
    if (hostname.includes('camera') || hostname.includes('cam') || ports.includes(554)) return 'camera';
    if (hostname.includes('server') || hostname.includes('srv') || ports.includes(22) || ports.includes(3389)) return 'server';
    if (hostname.includes('laptop') || hostname.includes('macbook') || hostname.includes('thinkpad')) return 'laptop';
    if (ports.includes(80) || ports.includes(443) || ports.includes(8080)) return 'web';
    return 'device';
  };

  // Device icon component
  const DeviceIcon = ({ type, className = "w-4 h-4" }) => {
    const icons = {
      router: Router,
      switch: Network,
      firewall: Shield,
      printer: Printer,
      nas: HardDrive,
      phone: Smartphone,
      camera: Monitor,
      server: Server,
      laptop: Laptop,
      web: Globe,
      device: Monitor
    };
    const Icon = icons[type] || Monitor;
    return <Icon className={className} />;
  };

  // Group hosts by subnet
  const groupedHosts = useMemo(() => {
    const groups = {};
    networkHosts.forEach(host => {
      const ip = host.ip_address || '';
      const parts = ip.split('.');
      const subnet = parts.length >= 3 ? `${parts[0]}.${parts[1]}.${parts[2]}.0/24` : t('common.unknown');
      if (!groups[subnet]) groups[subnet] = [];
      groups[subnet].push(host);
    });
    return groups;
  }, [networkHosts, t]);

  return (
    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
        {/* Welcome Banner */}
        <ClientsWelcomeBanner />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gradient-green flex items-center gap-3">
              <Building2 className="w-8 h-8" />
              {t('workspaces.title')}
            </h1>
            <p className="text-gray-400 mt-1">{`${t('workspaces.subtitle')}`}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${workspacesList.length >= 10 ? 'bg-red-900/40 text-red-400' : 'bg-gray-800 text-gray-400'}`}>
              {workspacesList.length}/10
            </span>
            {workspacesList.length >= 10 ? (
              <div className="relative group">
                <button
                  disabled
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 text-gray-400 cursor-not-allowed opacity-60"
                >
                  <Plus className="w-5 h-5" />
                  {t('workspaces.newWorkspace')}
                </button>
                <div className="absolute right-0 top-full mt-2 bg-gray-900 border border-red-500/30 text-red-400 text-xs px-3 py-2 rounded-lg whitespace-nowrap z-50 hidden group-hover:block shadow-lg">
                  Limite de 10 workspaces atteinte — supprimez-en un pour créer
                </div>
              </div>
            ) : (
              <PlanRestrictedButton
                feature="workspace_create"
                quotaType="workspaces"
                onClick={() => setShowCreateWizard(true)}
                className="btn-green flex items-center gap-2 px-4 py-2 rounded-lg"
                disabledClassName="!bg-gray-700 !text-gray-400"
                showLockIcon={true}
                tooltipPosition="bottom"
              >
                <Plus className="w-5 h-5" />
                {t('workspaces.newWorkspace')}
              </PlanRestrictedButton>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Workspaces List */}
          <div className="col-span-4 bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
            {/* Search */}
            <div className="p-4 border-b border-gray-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder={t('workspace.searchPlaceholder', 'Rechercher un workspace...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green focus:outline-none"
                />
              </div>
            </div>

            {/* List */}
            <div className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center text-gray-500">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                  {`${t('common.loading')}`}
                </div>
              ) : filteredWorkspaces.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">{t('workspaces.noWorkspaces')}</p>
                  <p className="text-sm mt-2 text-gray-400">{t('workspace.createFirst', 'Créer votre premier workspace')}</p>
                </div>
              ) : (
                filteredWorkspaces.map((workspace) => (
                  <div
                    key={workspace.id}
                    onClick={() => handleSelectWorkspace(workspace)}
                    className={`p-4 border-b border-gray-800 cursor-pointer transition-colors ${
                      selectedWorkspace?.id === workspace.id
                        ? 'bg-brand-green/10 border-l-4 border-l-brand-green'
                        : 'hover:bg-brand-black-lighter'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Workspace Logo */}
                      <div className="flex-shrink-0">
                        {workspace.logo_url ? (
                          <img
                            src={workspace.logo_url}
                            alt={`${workspace.name} logo`}
                            className="w-10 h-10 rounded-lg object-cover bg-brand-black border border-gray-700"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-10 h-10 rounded-lg bg-gradient-to-br from-brand-green/20 to-brand-green/5 border border-brand-green/30 flex items-center justify-center ${workspace.logo_url ? 'hidden' : ''}`}
                        >
                          <Building2 className="w-5 h-5 text-brand-green/70" />
                        </div>
                      </div>

                      {/* Workspace Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-white truncate">{workspace.name}</h3>
                          {workspace.name !== 'Administration' && (
                            workspace.contract_signed ? (
                              <CheckCircle className="w-4 h-4 text-brand-green flex-shrink-0 ml-2" title={t('workspaces.contractSigned', 'Contrat signé')} />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 ml-2" title={t('workspaces.pendingContract', 'Contrat en attente')} />
                            )
                          )}
                        </div>
                        {workspace.company_name && (
                          <p className="text-sm text-gray-400 truncate">{workspace.company_name}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          {workspace.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {workspace.city}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            {workspace.scans_count || 0} scans
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Workspace Details */}
          <div className="col-span-8">
            {selectedWorkspace ? (
              <div className="space-y-6">
                {/* Workspace Header */}
                <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedWorkspace.name}</h2>
                      {selectedWorkspace.company_name && (
                        <p className="text-gray-400 mt-1">{selectedWorkspace.company_name}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3">
                        {selectedWorkspace.city && (
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {selectedWorkspace.city}
                          </span>
                        )}
                        {selectedWorkspace.name !== 'Administration' && (
                          <span className={`px-2 py-1 text-xs rounded ${
                            selectedWorkspace.contract_signed
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {selectedWorkspace.contract_signed ? t('workspaces.contractSigned') : t('workspaces.pendingContract')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/workspaces/${selectedWorkspace.id}`)}
                        className="flex items-center gap-2 px-3 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 text-sm font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Voir détails
                      </button>
                      <button
                        onClick={handleEditWorkspace}
                        className="p-2 text-gray-400 hover:text-brand-green rounded-lg hover:bg-brand-black"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      {selectedWorkspace.name !== 'Administration' && (
                        <button
                          onClick={() => handleDeleteWorkspace(selectedWorkspace.id)}
                          className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-brand-black"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  {workspaceStats && (
                    <div className="grid grid-cols-4 gap-4 mt-6">
                      <div className="bg-brand-black p-4 rounded-lg border border-gray-800">
                        <div className="text-2xl font-bold text-cyan-400">{workspaceStats.scans_count}</div>
                        <div className="text-xs text-gray-500 truncate" title={t('workspaces.scansCount')}>{t('workspaces.scansCount')}</div>
                      </div>
                      <div className="bg-brand-black p-4 rounded-lg border border-gray-800">
                        <div className="text-2xl font-bold text-purple-400">{workspaceStats.schedules_count}</div>
                        <div className="text-xs text-gray-500 truncate" title={t('workspaces.schedulesCount')}>{t('workspaces.schedulesCount')}</div>
                      </div>
                      <div className="bg-brand-black p-4 rounded-lg border border-gray-800">
                        <div className="text-2xl font-bold text-brand-green">{workspaceStats.successful_executions}</div>
                        <div className="text-xs text-gray-500 truncate" title={t('workspaces.successfulRuns')}>{t('workspaces.successfulRuns')}</div>
                      </div>
                      <div className="bg-brand-black p-4 rounded-lg border border-gray-800">
                        <div className="text-2xl font-bold text-red-400">{workspaceStats.total_vulnerabilities}</div>
                        <div className="text-xs text-gray-500 truncate" title={t('workspaces.vulnerabilities')}>{t('workspaces.vulnerabilities')}</div>
                      </div>
                    </div>
                  )}

                  {/* AI Summary Section */}
                  <div className="mt-6 pt-6 border-t border-gray-800">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Brain className="w-5 h-5 text-purple-400" />
                        Résumé IA
                      </h3>
                      <button
                        onClick={() => fetchAiSummary(selectedWorkspace.id, aiSummary !== null)}
                        disabled={aiSummaryLoading}
                        className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 disabled:opacity-50"
                      >
                        {aiSummaryLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {t('common.generating', 'Génération...')}
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            {aiSummary ? t('common.regenerate', 'Régénérer') : t('workspaces.generateSummary', 'Générer le résumé')}
                          </>
                        )}
                      </button>
                    </div>

                    {aiSummaryLoading ? (
                      <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-6 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="relative">
                            <Brain className="w-12 h-12 text-purple-400 animate-pulse" />
                            <Loader2 className="w-6 h-6 text-brand-green absolute -bottom-1 -right-1 animate-spin" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{t('workspaces.analyzingInProgress', 'Analyse en cours...')}</p>
                            <p className="text-gray-400 text-sm mt-1">{t('workspaces.generatingAiSummary', 'Génération du résumé IA')}</p>
                          </div>
                        </div>
                      </div>
                    ) : aiSummary ? (
                      <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-4">
                        <MarkdownRenderer content={aiSummary.summary} variant="compact" />
                        {aiSummary.stats && (
                          <div className="mt-4 pt-4 border-t border-purple-500/20 flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {aiSummary.stats.scans_count} scans analysés
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {aiSummary.stats.hosts_count} hôtes
                            </span>
                            <span className="flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {aiSummary.stats.vulnerabilities_count} vulnérabilités
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-brand-black rounded-lg p-6 border border-gray-800 text-center">
                        <Brain className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                        <p className="text-gray-500 text-sm">
                          {t('workspaces.clickToGenerateSummary', 'Cliquez sur "Générer le résumé" pour obtenir une analyse IA du workspace')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Network Map Section */}
                <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Network className="w-5 h-5 text-cyan-400" />
                      {t('workspaces.networkMap', 'Carte Réseau')}
                    </h3>
                    <button
                      onClick={() => fetchNetworkHosts(selectedWorkspace.id)}
                      disabled={networkHostsLoading}
                      className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1 disabled:opacity-50"
                    >
                      {networkHostsLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Actualiser
                    </button>
                  </div>

                  {/* Network Stats */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-brand-black p-3 rounded-lg border border-gray-800 text-center">
                      <div className="text-xl font-bold text-cyan-400">{networkStats.total}</div>
                      <div className="text-xs text-gray-500">Hôtes</div>
                    </div>
                    <div className="bg-brand-black p-3 rounded-lg border border-gray-800 text-center">
                      <div className="text-xl font-bold text-red-400">{networkStats.vulnerable}</div>
                      <div className="text-xs text-gray-500">Vulnérables</div>
                    </div>
                    <div className="bg-brand-black p-3 rounded-lg border border-gray-800 text-center">
                      <div className="text-xl font-bold text-purple-400">{networkStats.subnets}</div>
                      <div className="text-xs text-gray-500">Sous-réseaux</div>
                    </div>
                    <div className="bg-brand-black p-3 rounded-lg border border-gray-800 text-center">
                      <div className="text-xl font-bold text-green-400">{networkStats.services}</div>
                      <div className="text-xs text-gray-500">Services</div>
                    </div>
                  </div>

                  {networkHostsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
                      <span className="ml-2 text-gray-400">{`${t('common.loading')}`}</span>
                    </div>
                  ) : networkHosts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Network className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>{t('workspaces.noHosts')}</p>
                      <p className="text-xs mt-1">{t('workspaces.launchScanToDiscoverNetwork')}</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                      {Object.entries(groupedHosts).map(([subnet, hosts]) => (
                        <div key={subnet} className="bg-brand-black rounded-lg border border-gray-800 overflow-hidden">
                          <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 px-4 py-2 border-b border-gray-800">
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-cyan-400 text-sm flex items-center gap-2">
                                <Wifi className="w-4 h-4" />
                                {subnet}
                              </span>
                              <span className="text-xs text-gray-500">{hosts.length} hôtes</span>
                            </div>
                          </div>
                          <div className="divide-y divide-gray-800">
                            {hosts.slice(0, 5).map((host, idx) => {
                              const deviceType = getDeviceType(host);
                              const hasVulns = (host.vulnerability_count || 0) > 0;
                              return (
                                <div key={idx} className="px-4 py-2 flex items-center justify-between hover:bg-brand-black-lighter">
                                  <div className="flex items-center gap-3">
                                    <div className={`p-1.5 rounded ${hasVulns ? 'bg-red-500/20 text-red-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
                                      <DeviceIcon type={deviceType} className="w-4 h-4" />
                                    </div>
                                    <div>
                                      <div className="font-mono text-sm text-white">{host.ip_address}</div>
                                      {host.hostname && host.hostname !== host.ip_address && (
                                        <div className="text-xs text-gray-500">{host.hostname}</div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    {host.open_ports && host.open_ports.length > 0 && (
                                      <span className="text-xs text-gray-500">
                                        {host.open_ports.slice(0, 3).join(', ')}
                                        {host.open_ports.length > 3 && ` +${host.open_ports.length - 3}`}
                                      </span>
                                    )}
                                    {hasVulns && (
                                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">
                                        {host.vulnerability_count} vulns
                                      </span>
                                    )}
                                    <span className={`w-2 h-2 rounded-full ${host.status === 'up' ? 'bg-green-500' : 'bg-gray-500'}`} />
                                  </div>
                                </div>
                              );
                            })}
                            {hosts.length > 5 && (
                              <div className="px-4 py-2 text-center text-xs text-gray-500">
                                + {hosts.length - 5} autres hôtes
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Schedules Section */}
                <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-brand-green" />
                      {t('workspaces.scanSchedules')}
                    </h3>
                    <button
                      onClick={() => setShowScheduleModal(true)}
                      className="text-sm text-brand-green hover:text-brand-green-light flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      {t('workspaces.addSchedule')}
                    </button>
                  </div>

                  {workspaceSchedules.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <Calendar className="w-16 h-16 mx-auto mb-4 text-brand-orange" />
                      <p className="text-lg font-medium text-gray-300 mb-2">{t('workspaces.noSchedules')}</p>
                      <p className="text-gray-400 text-base">{t('workspaces.noSchedulesHint')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {workspaceSchedules.map((schedule) => (
                        <div
                          key={schedule.id}
                          className="bg-brand-black p-4 rounded-lg border border-gray-800 flex items-center justify-between"
                        >
                          <div>
                            <div className="font-medium text-white">{schedule.name}</div>
                            <div className="text-sm text-gray-400">
                              {getFrequencyLabel(schedule.frequency)} - {getScanTypeLabel(schedule.scan_type)}
                            </div>
                            {schedule.next_run && (
                              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {t('workspaces.nextRun', 'Prochain')}: {new Date(schedule.next_run).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded ${
                              schedule.is_paused
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : schedule.is_active
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {schedule.is_paused ? t('scans.status.paused') : schedule.is_active ? t('common.active') : t('common.inactive')}
                            </span>
                            <button
                              onClick={() => handleRunScheduleNow(schedule.id)}
                              className="p-2 text-cyan-400 hover:bg-cyan-500/20 rounded"
                              title={t('workspaces.runNow')}
                            >
                              <Play className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleToggleSchedule(schedule)}
                              className="p-2 text-yellow-400 hover:bg-yellow-500/20 rounded"
                              title={schedule.is_paused ? t('scans.resumeScan') : t('scans.pauseScan')}
                            >
                              {schedule.is_paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Associated Scans Section */}
                <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Target className="w-5 h-5 text-brand-green" />
                      Scans associés
                    </h3>
                    <button
                      onClick={() => {
                        // Simple scan association - could be improved with modal
                        const unassociatedScans = availableScans.filter(
                          s => !workspaceScans.find(ws => ws.id === s.id)
                        );
                        if (unassociatedScans.length > 0) {
                          handleAssociateScans([unassociatedScans[0].id]);
                        } else {
                          toast.info(t('workspaces.noScansToAssociate', 'Aucun scan disponible à associer'));
                        }
                      }}
                      className="text-sm text-brand-green hover:text-brand-green-light flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      {t('workspaces.associateScan', 'Associer un scan')}
                    </button>
                  </div>

                  {workspaceScans.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>{t('workspaces.noScans')}</p>
                      <p className="text-gray-400 mt-1">{t('workspaces.noScansHint')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {workspaceScans.map((scan) => (
                        <div
                          key={scan.id}
                          onClick={() => navigate(`/scans/${scan.id}`)}
                          className="bg-brand-black p-3 rounded-lg border border-gray-800 flex items-center justify-between cursor-pointer hover:border-brand-green/50"
                        >
                          <div>
                            <div className="font-medium text-white">{scan.name || scan.target}</div>
                            <div className="text-xs text-gray-500">
                              {translateScanType(scan.scan_type)} - {scan.created_at && new Date(scan.created_at).toLocaleDateString('fr-FR')}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 text-xs rounded ${
                              scan.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                              scan.status === 'running' ? 'bg-blue-500/20 text-blue-400' :
                              scan.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {t(`scans.status.${scan.status}`) || scan.status}
                            </span>
                            {scan.vulnerabilities_count > 0 && (
                              <span className="text-xs text-red-400">
                                {scan.vulnerabilities_count} vulns
                              </span>
                            )}
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-brand-black-light rounded-xl border border-brand-green/30 p-12 text-center">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">{`${t('workspaces.select')}`}</h3>
                <p className="text-gray-500">{`${t('workspaces.selectDescription')}`}</p>
              </div>
            )}
          </div>
        </div>

        {/* Schedule Calendar Section */}
        <div className="mt-8">
          <ScheduleCalendar
            workspaces={workspacesList}
            onScheduleClick={(schedule) => {
              // Find and select the workspace containing this schedule
              const workspace = workspacesList.find(ws => ws.id === schedule.workspace_id);
              if (workspace) {
                handleSelectWorkspace(workspace);
              }
            }}
            onClientClick={(workspace) => {
              handleSelectWorkspace(workspace);
            }}
            onCreateSchedule={(date) => {
              // Open schedule wizard with the selected date
              setScheduleWizardDate(date);
              setShowScheduleWizard(true);
            }}
            onRunScanNow={(date) => {
              // Navigate to new scan page
              if (selectedWorkspace) {
                navigate(`/scans/new?workspace=${selectedWorkspace.id}`);
              } else {
                navigate('/scans/new');
              }
            }}
            onGenerateReport={(date) => {
              // Navigate to reports page
              navigate('/reports');
            }}
          />
        </div>
      </div>

      {/* Create Workspace Modal */}
      {showCreateModal && (
        <CreateWorkspaceModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchWorkspaces();
          }}
        />
      )}

      {/* Edit Workspace Modal */}
      {showEditModal && selectedWorkspace && (
        <EditWorkspaceModal
          workspace={selectedWorkspace}
          onClose={() => setShowEditModal(false)}
          onUpdated={(updatedWorkspace) => {
            setShowEditModal(false);
            setSelectedWorkspace(updatedWorkspace);
            fetchWorkspaces();
          }}
        />
      )}

      {/* Create Schedule Modal */}
      {showScheduleModal && selectedWorkspace && (
        <CreateScheduleModal
          workspaceId={selectedWorkspace.id}
          onClose={() => setShowScheduleModal(false)}
          onCreated={() => {
            setShowScheduleModal(false);
            fetchWorkspaceDetails(selectedWorkspace.id);
          }}
        />
      )}

      {/* Create Workspace Wizard */}
      <WorkspaceWizard
        isOpen={showCreateWizard}
        onClose={() => setShowCreateWizard(false)}
        atLimit={workspacesList.length >= 10}
        workspaceCount={workspacesList.length}
        onCreated={(workspace) => {
          setShowCreateWizard(false);
          fetchWorkspaces();
          // Navigate to the new workspace details
          if (workspace?.id) {
            navigate(`/workspaces/${workspace.id}`);
          }
        }}
      />

      {/* Schedule Scan Wizard */}
      {showScheduleWizard && (
        <ScheduleScanWizard
          isOpen={showScheduleWizard}
          onClose={() => {
            setShowScheduleWizard(false);
            setScheduleWizardDate(null);
          }}
          initialDate={scheduleWizardDate}
          workspaces={workspacesList}
          selectedWorkspace={selectedWorkspace}
          onScheduleCreated={() => {
            setShowScheduleWizard(false);
            setScheduleWizardDate(null);
            fetchWorkspaces();
            if (selectedWorkspace) {
              fetchWorkspaceDetails(selectedWorkspace.id);
            }
          }}
        />
      )}
    </div>
  );
};

// Country configuration for business registry lookup
const COUNTRY_CONFIG = {
  FR: {
    name: 'France',
    flag: '\ud83c\uddeb\ud83c\uddf7',
    identifierLabel: 'SIREN',
    identifierPlaceholder: '9 chiffres',
    identifierLength: 9,
    source: 'Pappers'
  },
  BE: {
    name: 'Belgique',
    flag: '\ud83c\udde7\ud83c\uddea',
    identifierLabel: 'BCE',
    identifierPlaceholder: '10 chiffres',
    identifierLength: 10,
    source: 'Pappers'
  },
  GB: {
    name: 'Royaume-Uni',
    flag: '\ud83c\uddec\ud83c\udde7',
    identifierLabel: 'Company Number',
    identifierPlaceholder: '8 caracteres',
    identifierLength: 8,
    source: 'Pappers'
  },
  CH: {
    name: 'Suisse',
    flag: '\ud83c\udde8\ud83c\udded',
    identifierLabel: 'IDE',
    identifierPlaceholder: 'CHE-xxx.xxx.xxx',
    identifierLength: 15,
    source: 'Pappers'
  },
  IT: {
    name: 'Italie',
    flag: '\ud83c\uddee\ud83c\uddf9',
    identifierLabel: 'Codice Fiscale',
    identifierPlaceholder: '11 ou 16 caracteres',
    identifierLength: 16,
    source: 'Italian Business Register'
  },
  ES: {
    name: 'Espagne',
    flag: '\ud83c\uddea\ud83c\uddf8',
    identifierLabel: 'NIF/CIF',
    identifierPlaceholder: '9 caracteres',
    identifierLength: 9,
    source: 'InfoNIF'
  },
  LU: {
    name: 'Luxembourg',
    flag: '\ud83c\uddf1\ud83c\uddfa',
    identifierLabel: 'RCS',
    identifierPlaceholder: 'B123456',
    identifierLength: 7,
    source: 'LBR'
  }
};

// Create Workspace Modal Component
const CreateWorkspaceModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    company_identifier: '',
    siren: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    country_code: 'FR',
    legal_form: '',
    naf_code: '',
    capital: '',
    employees_count: '',
    website: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_position: '',
    logo_base64: null,
    contract_signed: false,
    vpn_config_id: null,
    vpn_type: null,
    vpn_name: null,
    vpn_credentials: {}
  });
  const [loading, setLoading] = useState(false);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('company');
  const [vpnConfigs, setVpnConfigs] = useState({ externe: [], interne: [] });
  const [vpnLoading, setVpnLoading] = useState(false);

  // Company name search state
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchContainerRef = React.useRef(null);

  const currentCountry = COUNTRY_CONFIG[formData.country_code] || COUNTRY_CONFIG.FR;

  // Fetch VPN configs on mount
  useEffect(() => {
    const fetchVpnConfigs = async () => {
      try {
        setVpnLoading(true);
        const configs = await vpn.getConfigs();
        setVpnConfigs(configs);
      } catch (error) {
        console.error('Error fetching VPN configs:', error);
      } finally {
        setVpnLoading(false);
      }
    };
    fetchVpnConfigs();
  }, []);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle VPN selection
  const handleVpnSelect = (config) => {
    if (config) {
      setFormData(prev => ({
        ...prev,
        vpn_config_id: config.id,
        vpn_type: config.type,
        vpn_name: config.name,
        vpn_credentials: config.requires_auth ? prev.vpn_credentials : {}
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        vpn_config_id: null,
        vpn_type: null,
        vpn_name: null,
        vpn_credentials: {}
      }));
    }
  };

  // Handle country change
  const handleCountryChange = (countryCode) => {
    const countryInfo = COUNTRY_CONFIG[countryCode];
    setFormData(prev => ({
      ...prev,
      country_code: countryCode,
      country: countryInfo.name,
      company_identifier: '',
      siren: '',
      siret: ''
    }));
  };

  // Handle Business Registry lookup (multi-country)
  const handleRegistryLookup = async () => {
    const identifier = formData.company_identifier;
    if (!identifier || identifier.length < 5) {
      toast.error(t('workspaces.invalidIdentifier', 'Veuillez entrer un identifiant valide'));
      return;
    }

    try {
      setRegistryLoading(true);
      const company = await businessRegistry.lookup(formData.country_code, identifier);

      if (company) {
        setFormData(prev => ({
          ...prev,
          company_name: company.company_name || prev.company_name,
          siren: company.registration_number || prev.siren,
          siret: company.tax_id || prev.siret,
          address: company.address || prev.address,
          city: company.city || prev.city,
          postal_code: company.postal_code || prev.postal_code,
          country: company.country || prev.country,
          legal_form: company.legal_form || prev.legal_form,
          naf_code: company.activity_code ? `${company.activity_code} - ${company.activity_description || ''}` : prev.naf_code,
          capital: company.capital || prev.capital,
          employees_count: company.employees_count || prev.employees_count,
          website: company.website || prev.website,
          contact_email: company.contact_email || prev.contact_email,
          contact_phone: company.contact_phone || prev.contact_phone
        }));

        // Auto-fill workspace name if empty
        if (!formData.name && company.company_name) {
          setFormData(prev => ({
            ...prev,
            name: `${company.company_name} - Pentest ${new Date().getFullYear()}`
          }));
        }

        toast.success(`Informations ${currentCountry.source} chargees`);
      } else {
        toast.error(t('common.error'));
      }
    } catch (error) {
      console.error('Registry lookup error:', error);
      if (error.response?.status === 404) {
        toast.error(t('common.error'));
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setRegistryLoading(false);
    }
  };

  // Handle company name search with debounce
  const searchTimeoutRef = React.useRef(null);

  const handleCompanySearch = (searchValue) => {
    setCompanySearchTerm(searchValue);

    if (searchValue.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      setSearchLoading(false);
      return;
    }

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setSearchLoading(true);

    // Debounce search
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await businessRegistry.search(formData.country_code, searchValue, null, 10);
        setSearchResults(results || []);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Company search error:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  // Handle company selection from search results
  const handleSelectCompany = (company) => {
    setFormData(prev => ({
      ...prev,
      company_name: company.company_name || prev.company_name,
      siren: company.registration_number || prev.siren,
      siret: company.tax_id || prev.siret,
      address: company.address || prev.address,
      city: company.city || prev.city,
      postal_code: company.postal_code || prev.postal_code,
      country: company.country || prev.country,
      legal_form: company.legal_form || prev.legal_form,
      naf_code: company.activity_code ? `${company.activity_code} - ${company.activity_description || ''}` : prev.naf_code,
      capital: company.capital || prev.capital,
      employees_count: company.employees_count || prev.employees_count,
      website: company.website || prev.website,
      contact_email: company.contact_email || prev.contact_email,
      contact_phone: company.contact_phone || prev.contact_phone
    }));

    // Auto-fill workspace name if empty
    if (!formData.name && company.company_name) {
      setFormData(prev => ({
        ...prev,
        name: `${company.company_name} - Pentest ${new Date().getFullYear()}`
      }));
    }

    setCompanySearchTerm(company.company_name || '');
    setShowSearchResults(false);
    setSearchResults([]);
    toast.success(t('common.save'));
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('common.error'));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setLogoPreview(base64);
      setFormData(prev => ({ ...prev, logo_base64: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error(t('common.error'));
      return;
    }

    try {
      setLoading(true);
      await workspacesApi.create(formData);
      toast.success(t('workspaces.newWorkspace'));
      onCreated();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'company', label: 'Entreprise', icon: Building2 },
    { id: 'contact', label: 'Contact', icon: User },
    { id: 'logo', label: 'Logo', icon: Image },
    { id: 'vpn', label: 'VPN', icon: Shield },
    { id: 'contract', label: 'Contrat', icon: FileSignature }
  ];

  // Get selected VPN info
  const selectedVpnConfig = formData.vpn_config_id
    ? [...vpnConfigs.externe, ...vpnConfigs.interne].find(c => c.id === formData.vpn_config_id)
    : null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-brand-green" />
            Nouveau Client / Workspace
          </h2>
          <p className="text-sm text-gray-500 mt-1">Ajoutez un nouveau client avec recherche Pappers pour les entreprises françaises</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-brand-green border-b-2 border-brand-green bg-brand-green/5'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Tab: Entreprise */}
          {activeTab === 'company' && (
            <div className="space-y-4">
              {/* Multi-country Business Registry Lookup */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Search className="w-5 h-5 text-blue-400" />
                    <span className="font-medium text-blue-400">{`${t('workspaces.searchCompany')}`}</span>
                  </div>
                  <span className="text-xs text-gray-500">via {currentCountry.source}</span>
                </div>

                {/* Country Selector */}
                <div className="flex gap-2 mb-3">
                  {Object.entries(COUNTRY_CONFIG).map(([code, config]) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => handleCountryChange(code)}
                      className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-colors ${
                        formData.country_code === code
                          ? 'bg-blue-600 text-white'
                          : 'bg-brand-black border border-gray-700 text-gray-400 hover:border-blue-500'
                      }`}
                    >
                      <span>{config.flag}</span>
                      <span className="hidden sm:inline">{config.name}</span>
                    </button>
                  ))}
                </div>

                {/* Company Name Search */}
                <div className="relative" ref={searchContainerRef}>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={companySearchTerm}
                        onChange={(e) => handleCompanySearch(e.target.value)}
                        onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                        className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none pr-10"
                        placeholder="Rechercher par nom d'entreprise..."
                      />
                      {searchLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Results Dropdown */}
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-brand-dark border border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {searchResults.map((company, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleSelectCompany(company)}
                          className="w-full px-4 py-3 text-left hover:bg-brand-black transition-colors border-b border-gray-700 last:border-0"
                        >
                          <div className="text-white font-medium">{company.company_name}</div>
                          <div className="text-xs text-gray-400 mt-1 flex items-center gap-3">
                            {company.registration_number && (
                              <span>SIREN: {company.registration_number}</span>
                            )}
                            {company.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {company.city}
                              </span>
                            )}
                            {company.legal_form && (
                              <span>{company.legal_form}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* No results message */}
                  {showSearchResults && searchResults.length === 0 && companySearchTerm.length >= 2 && !searchLoading && (
                    <div className="absolute z-50 w-full mt-1 bg-brand-dark border border-gray-700 rounded-lg shadow-lg p-4 text-center text-gray-400">
                      Aucune entreprise trouvee pour "{companySearchTerm}"
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500 mt-2 break-words">
                  Entrez le nom de l'entreprise pour rechercher dans le registre {currentCountry.source}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.workspaceName')} *`}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Ex: Entreprise XYZ - Pentest Q4 2024"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.companyName')}`}</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Dénomination sociale complète"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('workspaces.siret')}</label>
                  <input
                    type="text"
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="14 chiffres"
                    maxLength={14}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.legalForm')}`}</label>
                  <input
                    type="text"
                    value={formData.legal_form}
                    onChange={(e) => setFormData({ ...formData, legal_form: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder:text-gray-500 placeholder:italic focus:border-brand-green focus:outline-none"
                    placeholder="SAS, SARL, SA..."
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.address')}`}</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Numéro et rue"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.postalCode')}`}</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="75001"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.city')}`}</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Paris"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.nafCode')}`}</label>
                  <input
                    type="text"
                    value={formData.naf_code}
                    onChange={(e) => setFormData({ ...formData, naf_code: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="6201Z - Programmation informatique"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.capital')}`}</label>
                  <input
                    type="text"
                    value={formData.capital}
                    onChange={(e) => setFormData({ ...formData, capital: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="10 000 EUR"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.employees')}`}</label>
                  <input
                    type="text"
                    value={formData.employees_count}
                    onChange={(e) => setFormData({ ...formData, employees_count: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="10-50 salariés"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.website')}`}</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="https://exemple.com"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Contact */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  <span className="font-medium text-purple-400">{`${t('workspaces.mainContact')}`}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('workspaces.contactResponsibleDescription')}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                    <User className="w-3 h-3" /> {t('workspaces.contactFullName')}
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="Jean Dupont"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                    <Briefcase className="w-3 h-3" /> {t('workspaces.contactFunction')}
                  </label>
                  <input
                    type="text"
                    value={formData.contact_position}
                    onChange={(e) => setFormData({ ...formData, contact_position: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="RSSI, DSI, CTO..."
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {t('workspaces.contactEmail')}
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="contact@exemple.fr"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {t('workspaces.contactPhone')}
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Logo */}
          {activeTab === 'logo' && (
            <div className="space-y-4">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-cyan-400" />
                  <span className="font-medium text-cyan-400">{`${t('workspaces.companyLogo')}`}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{t('workspaces.logoUsedInReports')}</p>
              </div>

              <div className="flex flex-col items-center gap-4">
                {/* Logo Preview */}
                <div className="w-48 h-48 bg-brand-black border-2 border-dashed border-gray-700 rounded-xl flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="text-center text-gray-500">
                      <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <span className="text-sm">{`${t('workspaces.noLogo')}`}</span>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                    Choisir un fichier
                  </div>
                </label>
                <p className="text-xs text-gray-500">PNG, JPG ou SVG. Max 2MB.</p>

                {logoPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setLogoPreview(null);
                      setFormData(prev => ({ ...prev, logo_base64: null }));
                    }}
                    className="text-sm text-red-400 hover:text-red-300"
                  >
                    Supprimer le logo
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Tab: VPN */}
          {activeTab === 'vpn' && (
            <div className="space-y-4">
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-400" />
                  <span className="font-medium text-purple-400">{`${t('workspaces.vpnConfiguration')}`}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 break-words">Associer un VPN interne ou externe pour les scans de ce client</p>
              </div>

              {vpnLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
                  <span className="ml-2 text-gray-400">{`${t('common.loading')}`}</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* No VPN Option */}
                  <div
                    onClick={() => handleVpnSelect(null)}
                    className={`bg-brand-black rounded-lg p-4 border cursor-pointer transition-all ${
                      !formData.vpn_config_id
                        ? 'border-brand-green bg-brand-green/5'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 ${!formData.vpn_config_id ? 'border-brand-green bg-brand-green' : 'border-gray-600'}`} />
                      <div className="min-w-0">
                        <span className="text-white font-medium">{`${t('workspaces.noVPN')}`}</span>
                        <p className="text-xs text-gray-500 break-words">Les scans utiliseront la connexion directe</p>
                      </div>
                    </div>
                  </div>

                  {/* Agent Windows Download */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Server className="w-5 h-5 text-blue-400" />
                          <span className="font-medium text-blue-400">Agent Windows</span>
                        </div>
                        <p className="text-sm text-gray-300 mb-2">
                          Scannez votre réseau interne sans VPN grâce à notre agent léger installable sur Windows.
                        </p>
                        <ul className="text-xs text-gray-400 space-y-1 mb-3">
                          <li className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            Installation simple (ZIP ~16 KB)
                          </li>
                          <li className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            Aucune configuration VPN requise
                          </li>
                          <li className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            Tunnel sécurisé (HTTPS + HMAC)
                          </li>
                          <li className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            Compatible Windows 10/11/Server
                          </li>
                        </ul>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadInstaller(selectedWorkspace?.id); }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                          >
                            <Download className="w-4 h-4" />
                            Télécharger l'Agent
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate('/agents')}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-black border border-gray-700 hover:border-blue-500 text-gray-300 rounded-lg transition-colors text-sm"
                          >
                            <Server className="w-4 h-4" />
                            Gérer les Agents
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Internal VPNs */}
                  {vpnConfigs.interne.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                        <Network className="w-4 h-4" />
                        VPN Internes ({vpnConfigs.interne.length})
                      </h4>
                      <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                        {vpnConfigs.interne.map((config) => (
                          <div
                            key={config.id}
                            onClick={() => handleVpnSelect(config)}
                            className={`bg-brand-black rounded-lg p-3 border cursor-pointer transition-all ${
                              formData.vpn_config_id === config.id
                                ? 'border-brand-green bg-brand-green/5'
                                : 'border-gray-700 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <div className={`w-3 h-3 rounded-full border-2 mt-1 ${formData.vpn_config_id === config.id ? 'border-brand-green bg-brand-green' : 'border-gray-600'}`} />
                              <div className="flex-1 min-w-0">
                                <span className="text-white text-sm font-medium block truncate">{config.name}</span>
                                <span className="text-xs text-gray-500">{config.provider} - {config.protocol?.toUpperCase()}</span>
                                {config.requires_auth && (
                                  <span className="inline-flex items-center gap-1 text-xs text-yellow-400 mt-1">
                                    <Lock className="w-3 h-3" /> Auth requise
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Credentials if required */}
                  {selectedVpnConfig?.requires_auth && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Lock className="w-4 h-4 text-yellow-400" />
                        <span className="text-yellow-400 font-medium">Identifiants VPN requis</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Nom d'utilisateur</label>
                          <input
                            type="text"
                            value={formData.vpn_credentials?.username || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              vpn_credentials: { ...prev.vpn_credentials, username: e.target.value }
                            }))}
                            className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                            placeholder="utilisateur_vpn"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Mot de passe</label>
                          <input
                            type="password"
                            value={formData.vpn_credentials?.password || ''}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              vpn_credentials: { ...prev.vpn_credentials, password: e.target.value }
                            }))}
                            className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                            placeholder="••••••••"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No VPNs available */}
                  {vpnConfigs.externe.length === 0 && vpnConfigs.interne.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Aucune configuration VPN disponible</p>
                      <p className="text-sm mt-1">Ajoutez des fichiers .ovpn dans les paramètres</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab: Contrat */}
          {activeTab === 'contract' && (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-green-400" />
                  <span className="font-medium text-green-400">{`${t('workspaces.contractStatus')}`}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Autorisation de test d'intrusion</p>
              </div>

              <div className="bg-brand-black rounded-lg p-6 border border-gray-700">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.contract_signed}
                    onChange={(e) => setFormData({ ...formData, contract_signed: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-brand-green focus:ring-brand-green"
                  />
                  <div>
                    <span className="text-white font-medium">{`${t('workspaces.contractSigned')}`}</span>
                    <p className="text-xs text-gray-500">Le client a signé l'autorisation de pentest</p>
                  </div>
                </label>
              </div>

              {/* Bouton d'envoi de contrat électronique */}
              <div className="bg-brand-black rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-medium">{`${t('workspaces.sendContract')}`}</span>
                    <p className="text-xs text-gray-500 mt-1">Envoyez une demande de signature électronique au client</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.contact_email) {
                        toast.error(t('common.error'));
                        return;
                      }
                      toast.info('Fonctionnalité d\'envoi de contrat électronique disponible après création du workspace');
                    }}
                    className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Envoyer
                  </button>
                </div>
              </div>

              {!formData.contract_signed && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    <span className="text-yellow-400 font-medium">Attention</span>
                  </div>
                  <p className="text-sm text-gray-400 mt-2">
                    Sans contrat signe, les scans ne pourront pas etre lances.
                    Vous pourrez generer et envoyer le contrat apres la creation du workspace.
                  </p>
                </div>
              )}

              {formData.contract_signed && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-medium">{`${t('workspaces.readyForTests')}`}</span>
                  </div>
                  <p className="text-sm text-gray-400 mt-2">
                    Le workspace sera créé avec l'autorisation de pentest validée.
                  </p>
                </div>
              )}
            </div>
          )}
        </form>

        {/* Footer Actions */}
        <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-800">
          <div className="text-xs text-gray-500">
            {formData.company_name && <span>Client: {formData.company_name}</span>}
            {formData.vpn_name && <span className="ml-2">| VPN: {formData.vpn_name}</span>}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.name}
              className="btn-green px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Créer le workspace
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// VPN Protocol options for edit modal
const VPN_PROTOCOLS_EDIT = {
  openvpn: { name: 'OpenVPN', icon: '🔐', description: 'Fichier .ovpn', fileExt: '.ovpn', color: 'blue' },
  wireguard: { name: 'WireGuard', icon: '⚡', description: 'VPN moderne', fileExt: '.conf', color: 'purple' },
  l2tp: { name: 'L2TP/IPSec', icon: '🛡️', description: 'Windows natif', fileExt: null, color: 'orange' }
};

// Edit Workspace Modal Component
const EditWorkspaceModal = ({ workspace, onClose, onUpdated }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: workspace.name || '',
    company_name: workspace.company_name || '',
    company_identifier: workspace.siren || '',
    siren: workspace.siren || '',
    siret: workspace.siret || '',
    address: workspace.address || '',
    city: workspace.city || '',
    postal_code: workspace.postal_code || '',
    country: workspace.country || 'France',
    country_code: workspace.country_code || 'FR',
    legal_form: workspace.legal_form || '',
    naf_code: workspace.naf_code || '',
    capital: workspace.capital || '',
    employees_count: workspace.employees_count || '',
    website: workspace.website || '',
    contact_name: workspace.contact_name || '',
    contact_email: workspace.contact_email || '',
    contact_phone: workspace.contact_phone || '',
    contact_position: workspace.contact_position || '',
    logo_base64: workspace.logo_base64 || null,
    contract_signed: workspace.contract_signed || false,
    vpn_config_id: workspace.vpn_config_id || null,
    vpn_type: workspace.vpn_type || null,
    vpn_name: workspace.vpn_name || null,
    vpn_protocol: workspace.vpn_protocol || 'openvpn',
    vpn_credentials: workspace.vpn_credentials || {},
    vpn_config_data: workspace.vpn_config_data || {}
  });
  const [showVpnPassword, setShowVpnPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(workspace.logo_base64 || null);
  const [activeTab, setActiveTab] = useState('company');
  const [vpnConfigs, setVpnConfigs] = useState({ externe: [], interne: [] });
  const [vpnLoading, setVpnLoading] = useState(false);
  const [vpnUploadFile, setVpnUploadFile] = useState(null);
  const [vpnUploading, setVpnUploading] = useState(false);

  // Update form data when workspace changes
  useEffect(() => {
    if (workspace) {
      setFormData({
        name: workspace.name || '',
        company_name: workspace.company_name || '',
        company_identifier: workspace.siren || '',
        siren: workspace.siren || '',
        siret: workspace.siret || '',
        address: workspace.address || '',
        city: workspace.city || '',
        postal_code: workspace.postal_code || '',
        country: workspace.country || 'France',
        country_code: workspace.country_code || 'FR',
        legal_form: workspace.legal_form || '',
        naf_code: workspace.naf_code || '',
        capital: workspace.capital || '',
        employees_count: workspace.employees_count || '',
        website: workspace.website || '',
        contact_name: workspace.contact_name || '',
        contact_email: workspace.contact_email || '',
        contact_phone: workspace.contact_phone || '',
        contact_position: workspace.contact_position || '',
        logo_base64: workspace.logo_base64 || null,
        contract_signed: workspace.contract_signed || false,
        vpn_config_id: workspace.vpn_config_id || null,
        vpn_type: workspace.vpn_type || null,
        vpn_name: workspace.vpn_name || null,
        vpn_protocol: workspace.vpn_protocol || 'openvpn',
        vpn_credentials: workspace.vpn_credentials || {},
        vpn_config_data: workspace.vpn_config_data || {}
      });
      setLogoPreview(workspace.logo_base64 || null);
    }
  }, [workspace]);

  // Fetch VPN configs on mount
  useEffect(() => {
    const fetchVpnConfigs = async () => {
      try {
        setVpnLoading(true);
        const configs = await vpn.getConfigs();
        setVpnConfigs(configs);
      } catch (error) {
        console.error('Error fetching VPN configs:', error);
      } finally {
        setVpnLoading(false);
      }
    };
    fetchVpnConfigs();
  }, []);

  // Handle VPN selection
  const handleVpnSelect = (config) => {
    if (config) {
      setFormData(prev => ({
        ...prev,
        vpn_config_id: config.id,
        vpn_type: config.type,
        vpn_name: config.name,
        vpn_credentials: config.requires_auth ? prev.vpn_credentials : {}
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        vpn_config_id: null,
        vpn_type: null,
        vpn_name: null,
        vpn_credentials: {}
      }));
    }
  };

  // Get selected VPN info
  const selectedVpnConfig = formData.vpn_config_id && formData.vpn_config_id !== 'pending_upload'
    ? [...vpnConfigs.externe, ...vpnConfigs.interne].find(c => c.id === formData.vpn_config_id)
    : null;

  // Handle VPN file selection for upload (OpenVPN or WireGuard)
  const handleVpnFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isOpenVPN = file.name.endsWith('.ovpn');
    const isWireGuard = file.name.endsWith('.conf');

    if (!isOpenVPN && !isWireGuard) {
      toast.error(t('common.error'));
      return;
    }

    // Detect protocol from file extension
    const detectedProtocol = isOpenVPN ? 'openvpn' : 'wireguard';
    setFormData(prev => ({ ...prev, vpn_protocol: detectedProtocol }));
    setVpnUploadFile(file);
    toast.success(`Fichier ${VPN_PROTOCOLS_EDIT[detectedProtocol].name} "${file.name}" selectionne`);
  };

  // Upload VPN file
  const handleVpnUpload = async () => {
    if (!vpnUploadFile) return;

    try {
      setVpnUploading(true);

      // Use uploadMultiProtocol for all VPN types (supports .ovpn, .conf, etc.)
      const protocol = formData.vpn_protocol || 'openvpn';
      const result = await vpn.uploadMultiProtocol(vpnUploadFile, workspace.id, protocol);

      // Refresh VPN configs list
      const configs = await vpn.getConfigs(null, workspace.id);
      setVpnConfigs(configs);

      // Select the newly uploaded config
      if (result.config) {
        setFormData(prev => ({
          ...prev,
          vpn_config_id: result.config.id,
          vpn_type: result.config.type || 'interne',
          vpn_name: result.config.name,
          vpn_protocol: result.config.protocol || prev.vpn_protocol,
          vpn_credentials: {}
        }));
      }

      setVpnUploadFile(null);
      toast.success(`Configuration ${VPN_PROTOCOLS_EDIT[formData.vpn_protocol]?.name || 'VPN'} uploadee avec succes`);
    } catch (error) {
      console.error('VPN upload error:', error);
      const errorMsg = translateBackendError(error.response?.data?.detail) || 'Erreur lors de l\'upload du VPN';
      toast.error(errorMsg);
    } finally {
      setVpnUploading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('common.error'));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setLogoPreview(base64);
      setFormData(prev => ({ ...prev, logo_base64: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error(t('common.error'));
      return;
    }

    try {
      setLoading(true);
      const updatedWorkspace = await workspacesApi.update(workspace.id, formData);
      toast.success(t('workspaces.editWorkspace'));
      onUpdated(updatedWorkspace);
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const baseTabs = [
    { id: 'company', label: 'Entreprise', icon: Building2 },
    { id: 'contact', label: 'Contact', icon: User },
    { id: 'logo', label: 'Logo', icon: Image },
    { id: 'vpn', label: 'VPN', icon: Shield },
    { id: 'contract', label: 'Contrat', icon: FileSignature }
  ];

  // Hide contract tab for Administration workspace
  const tabs = workspace.name === 'Administration'
    ? baseTabs.filter(tab => tab.id !== 'contract')
    : baseTabs;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Edit2 className="w-6 h-6 text-brand-green" />
            Modifier le workspace
          </h2>
          <p className="text-sm text-gray-500 mt-1">Modifier les informations du client "{workspace.name}"</p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-brand-green border-b-2 border-brand-green bg-brand-green/5'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Tab: Entreprise */}
          {activeTab === 'company' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.workspaceName')} *`}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.companyName')}`}</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('workspaces.siren')}</label>
                  <input
                    type="text"
                    value={formData.siren}
                    onChange={(e) => setFormData({ ...formData, siren: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    maxLength={9}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('workspaces.siret')}</label>
                  <input
                    type="text"
                    value={formData.siret}
                    onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                    maxLength={14}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.legalForm')}`}</label>
                  <input
                    type="text"
                    value={formData.legal_form}
                    onChange={(e) => setFormData({ ...formData, legal_form: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t('workspaces.country')}</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.address')}`}</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.postalCode')}`}</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.city')}`}</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.nafCode')}`}</label>
                  <input
                    type="text"
                    value={formData.naf_code}
                    onChange={(e) => setFormData({ ...formData, naf_code: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.capital')}`}</label>
                  <input
                    type="text"
                    value={formData.capital}
                    onChange={(e) => setFormData({ ...formData, capital: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.employees')}`}</label>
                  <input
                    type="text"
                    value={formData.employees_count}
                    onChange={(e) => setFormData({ ...formData, employees_count: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{`${t('workspaces.website')}`}</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Contact */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  <span className="font-medium text-purple-400">{`${t('workspaces.mainContact')}`}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                    <User className="w-3 h-3" /> {t('workspaces.contactFullName')}
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                    <Briefcase className="w-3 h-3" /> {t('workspaces.contactFunction')}
                  </label>
                  <input
                    type="text"
                    value={formData.contact_position}
                    onChange={(e) => setFormData({ ...formData, contact_position: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {t('workspaces.contactEmail')}
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {t('workspaces.contactPhone')}
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab: Logo */}
          {activeTab === 'logo' && (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-4">
                <div className="w-48 h-48 bg-brand-black border-2 border-dashed border-gray-700 rounded-xl flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="text-center text-gray-500">
                      <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <span className="text-sm">{`${t('workspaces.noLogo')}`}</span>
                    </div>
                  )}
                </div>

                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                    Choisir un fichier
                  </div>
                </label>
                <p className="text-xs text-gray-500">PNG, JPG ou SVG. Max 2MB.</p>

                {logoPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setLogoPreview(null);
                      setFormData(prev => ({ ...prev, logo_base64: null }));
                    }}
                    className="text-sm text-red-400 hover:text-red-300"
                  >
                    Supprimer le logo
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Tab: VPN */}
          {activeTab === 'vpn' && (
            <div className="space-y-4">
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-400" />
                  <span className="font-medium text-purple-400">{`${t('workspaces.vpnConfiguration')}`}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 break-words">Modifier le VPN associé pour les scans de ce client</p>
              </div>

              {vpnLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
                  <span className="ml-2 text-gray-400">{`${t('common.loading')}`}</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* No VPN Option */}
                  <div
                    onClick={() => handleVpnSelect(null)}
                    className={`bg-brand-black rounded-lg p-4 border cursor-pointer transition-all ${
                      !formData.vpn_config_id
                        ? 'border-brand-green bg-brand-green/5'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 ${!formData.vpn_config_id ? 'border-brand-green bg-brand-green' : 'border-gray-600'}`} />
                      <div className="min-w-0">
                        <span className="text-white font-medium">{`${t('workspaces.noVPN')}`}</span>
                        <p className="text-xs text-gray-500 break-words">Les scans utiliseront la connexion directe</p>
                      </div>
                    </div>
                  </div>

                  {/* Agent Windows Download */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Server className="w-5 h-5 text-blue-400" />
                          <span className="font-medium text-blue-400">Agent Windows</span>
                        </div>
                        <p className="text-sm text-gray-300 mb-2">
                          Scannez votre réseau interne sans VPN grâce à notre agent léger installable sur Windows.
                        </p>
                        <ul className="text-xs text-gray-400 space-y-1 mb-3">
                          <li className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            Installation simple (ZIP ~16 KB)
                          </li>
                          <li className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            Aucune configuration VPN requise
                          </li>
                          <li className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            Tunnel sécurisé (HTTPS + HMAC)
                          </li>
                          <li className="flex items-center gap-1.5">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            Compatible Windows 10/11/Server
                          </li>
                        </ul>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); downloadInstaller(selectedWorkspace?.id); }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                          >
                            <Download className="w-4 h-4" />
                            Télécharger l'Agent
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate('/agents')}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-black border border-gray-700 hover:border-blue-500 text-gray-300 rounded-lg transition-colors text-sm"
                          >
                            <Server className="w-4 h-4" />
                            Gérer les Agents
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* VPN Protocol Selection */}
                  <div className="border-t border-gray-700 pt-4">
                    <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                      <Server className="w-4 h-4" />
                      Choisir le protocole VPN
                    </h4>
                    <div className="flex gap-2 mb-4">
                      {Object.entries(VPN_PROTOCOLS_EDIT).map(([key, proto]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            setVpnUploadFile(null);
                            setFormData(prev => ({
                              ...prev,
                              vpn_protocol: key,
                              vpn_config_data: key === prev.vpn_protocol ? prev.vpn_config_data : {},
                              vpn_credentials: key === prev.vpn_protocol ? prev.vpn_credentials : {}
                            }));
                          }}
                          className={`flex-1 px-3 py-2 rounded-lg border text-center transition-all ${
                            formData.vpn_protocol === key
                              ? `border-${proto.color}-500 bg-${proto.color}-500/10 text-white`
                              : 'border-gray-700 hover:border-gray-600 text-gray-400'
                          }`}
                          style={formData.vpn_protocol === key ? {
                            borderColor: proto.color === 'blue' ? '#3b82f6' : proto.color === 'purple' ? '#a855f7' : '#f97316',
                            backgroundColor: proto.color === 'blue' ? 'rgba(59,130,246,0.1)' : proto.color === 'purple' ? 'rgba(168,85,247,0.1)' : 'rgba(249,115,22,0.1)'
                          } : {}}
                        >
                          <div className="text-lg">{proto.icon}</div>
                          <div className="font-medium text-xs">{proto.name}</div>
                        </button>
                      ))}
                    </div>

                    {/* OpenVPN Configuration */}
                    {formData.vpn_protocol === 'openvpn' && (
                      <div className="space-y-4">
                        {/* Internal VPNs */}
                        {vpnConfigs.interne.filter(c => c.protocol === 'openvpn' || !c.protocol).length > 0 && (
                          <div>
                            <h5 className="text-xs font-medium text-gray-500 mb-2">VPN OpenVPN existants</h5>
                            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                              {vpnConfigs.interne.filter(c => c.protocol === 'openvpn' || !c.protocol).map((config) => (
                                <div
                                  key={config.id}
                                  onClick={() => handleVpnSelect(config)}
                                  className={`bg-brand-black rounded-lg p-2 border cursor-pointer transition-all ${
                                    formData.vpn_config_id === config.id
                                      ? 'border-brand-green bg-brand-green/5'
                                      : 'border-gray-700 hover:border-gray-600'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${formData.vpn_config_id === config.id ? 'bg-brand-green' : 'bg-gray-600'}`} />
                                    <span className="text-white text-xs font-medium truncate">{config.name}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Upload OpenVPN */}
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                          <h5 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            Uploader un fichier OpenVPN
                          </h5>
                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <input
                                type="file"
                                accept=".ovpn"
                                onChange={handleVpnFileSelect}
                                className="hidden"
                                id="vpn-upload-edit"
                              />
                              <label
                                htmlFor="vpn-upload-edit"
                                className="flex items-center justify-center w-full px-4 py-3 bg-brand-black border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-blue-500 transition-colors"
                              >
                                {vpnUploadFile ? (
                                  <div className="flex items-center gap-2 text-blue-400">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-sm">{vpnUploadFile.name}</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 text-gray-400">
                                    <Upload className="w-4 h-4" />
                                    <span className="text-sm">Fichier .ovpn</span>
                                  </div>
                                )}
                              </label>
                            </div>
                            <button
                              type="button"
                              onClick={handleVpnUpload}
                              disabled={!vpnUploadFile || vpnUploading}
                              className="px-4 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {vpnUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                              {vpnUploading ? 'Upload...' : t('workspaces.upload')}
                            </button>
                          </div>
                        </div>

                        {/* OpenVPN Credentials */}
                        {(selectedVpnConfig?.requires_auth || vpnUploadFile) && (
                          <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                            <h5 className="text-sm font-medium text-blue-300 mb-3 flex items-center gap-2">
                              <Key className="w-4 h-4" />
                              Identifiants OpenVPN (optionnel)
                            </h5>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Utilisateur</label>
                                <input
                                  type="text"
                                  value={formData.vpn_credentials?.username || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_credentials: { ...prev.vpn_credentials, username: e.target.value }
                                  }))}
                                  className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Mot de passe</label>
                                <div className="relative">
                                  <input
                                    type={showVpnPassword ? 'text' : 'password'}
                                    value={formData.vpn_credentials?.password || ''}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      vpn_credentials: { ...prev.vpn_credentials, password: e.target.value }
                                    }))}
                                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none pr-10"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowVpnPassword(!showVpnPassword)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                                  >
                                    {showVpnPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* WireGuard Configuration */}
                    {formData.vpn_protocol === 'wireguard' && (
                      <div className="space-y-4">
                        {/* Mode selection */}
                        <div className="flex gap-2 mb-4">
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, vpn_config_data: { ...prev.vpn_config_data, configMode: 'file' }}))}
                            className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                              (formData.vpn_config_data?.configMode || 'file') === 'file'
                                ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                                : 'border-gray-700 text-gray-400 hover:border-gray-600'
                            }`}
                          >
                            <Upload className="w-4 h-4 inline mr-2" />
                            Fichier .conf
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, vpn_config_data: { ...prev.vpn_config_data, configMode: 'manual' }}))}
                            className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                              formData.vpn_config_data?.configMode === 'manual'
                                ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                                : 'border-gray-700 text-gray-400 hover:border-gray-600'
                            }`}
                          >
                            <Key className="w-4 h-4 inline mr-2" />
                            Configuration manuelle
                          </button>
                        </div>

                        {(formData.vpn_config_data?.configMode || 'file') === 'file' ? (
                          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                            <h5 className="text-sm font-medium text-purple-400 mb-3 flex items-center gap-2">
                              <Upload className="w-4 h-4" />
                              Uploader un fichier WireGuard
                            </h5>
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <input
                                  type="file"
                                  accept=".conf"
                                  onChange={handleVpnFileSelect}
                                  className="hidden"
                                  id="vpn-upload-wg"
                                />
                                <label
                                  htmlFor="vpn-upload-wg"
                                  className="flex items-center justify-center w-full px-4 py-3 bg-brand-black border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-purple-500 transition-colors"
                                >
                                  {vpnUploadFile ? (
                                    <div className="flex items-center gap-2 text-purple-400">
                                      <CheckCircle className="w-4 h-4" />
                                      <span className="text-sm">{vpnUploadFile.name}</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 text-gray-400">
                                      <Upload className="w-4 h-4" />
                                      <span className="text-sm">Fichier .conf</span>
                                    </div>
                                  )}
                                </label>
                              </div>
                              <button
                                type="button"
                                onClick={handleVpnUpload}
                                disabled={!vpnUploadFile || vpnUploading}
                                className="px-4 py-3 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                              >
                                {vpnUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                {vpnUploading ? 'Upload...' : t('workspaces.upload')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 space-y-4">
                            <h5 className="text-sm font-medium text-purple-400 flex items-center gap-2">
                              <Key className="w-4 h-4" />
                              Configuration WireGuard manuelle
                            </h5>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="col-span-2">
                                <label className="block text-xs text-gray-400 mb-1">Endpoint (serveur:port) *</label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data?.endpoint || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_config_id: 'pending_manual',
                                    vpn_type: 'interne',
                                    vpn_name: 'WireGuard VPN',
                                    vpn_config_data: { ...prev.vpn_config_data, endpoint: e.target.value }
                                  }))}
                                  className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                                  placeholder="vpn.client.com:51820"
                                />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-xs text-gray-400 mb-1">Clé publique serveur *</label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data?.public_key || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_config_data: { ...prev.vpn_config_data, public_key: e.target.value }
                                  }))}
                                  className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm font-mono focus:border-purple-500 focus:outline-none"
                                  placeholder="Base64..."
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Adresse IP client</label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data?.address || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_config_data: { ...prev.vpn_config_data, address: e.target.value }
                                  }))}
                                  className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                                  placeholder="10.0.0.2/24"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">DNS</label>
                                <input
                                  type="text"
                                  value={formData.vpn_config_data?.dns || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    vpn_config_data: { ...prev.vpn_config_data, dns: e.target.value }
                                  }))}
                                  className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-purple-500 focus:outline-none"
                                  placeholder="1.1.1.1"
                                />
                              </div>
                              <div className="col-span-2">
                                <label className="block text-xs text-gray-400 mb-1">Clé privée client *</label>
                                <div className="relative">
                                  <input
                                    type={showVpnPassword ? 'text' : 'password'}
                                    value={formData.vpn_credentials?.private_key || ''}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      vpn_credentials: { ...prev.vpn_credentials, private_key: e.target.value }
                                    }))}
                                    className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm font-mono focus:border-purple-500 focus:outline-none pr-10"
                                    placeholder="Base64..."
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowVpnPassword(!showVpnPassword)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                                  >
                                    {showVpnPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* L2TP/IPSec Configuration */}
                    {formData.vpn_protocol === 'l2tp' && (
                      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 space-y-4">
                        <h5 className="text-sm font-medium text-orange-400 flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          Configuration L2TP/IPSec
                        </h5>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-400 mb-1">Adresse serveur VPN *</label>
                            <input
                              type="text"
                              value={formData.vpn_config_data?.server || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                vpn_config_id: 'pending_manual',
                                vpn_type: 'interne',
                                vpn_name: 'L2TP VPN',
                                vpn_config_data: { ...prev.vpn_config_data, server: e.target.value }
                              }))}
                              className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                              placeholder="vpn.client.com"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-400 mb-1">Clé pré-partagée (PSK) *</label>
                            <div className="relative">
                              <input
                                type={showVpnPassword ? 'text' : 'password'}
                                value={formData.vpn_config_data?.psk || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  vpn_config_data: { ...prev.vpn_config_data, psk: e.target.value }
                                }))}
                                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none pr-10"
                                placeholder="Clé IPSec"
                              />
                              <button
                                type="button"
                                onClick={() => setShowVpnPassword(!showVpnPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                              >
                                {showVpnPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Utilisateur *</label>
                            <input
                              type="text"
                              value={formData.vpn_credentials?.username || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                vpn_credentials: { ...prev.vpn_credentials, username: e.target.value }
                              }))}
                              className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Mot de passe *</label>
                            <div className="relative">
                              <input
                                type={showVpnPassword ? 'text' : 'password'}
                                value={formData.vpn_credentials?.password || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  vpn_credentials: { ...prev.vpn_credentials, password: e.target.value }
                                }))}
                                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowVpnPassword(!showVpnPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
                              >
                                {showVpnPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab: Contrat */}
          {activeTab === 'contract' && (
            <div className="space-y-4">
              <div className="bg-brand-black rounded-lg p-6 border border-gray-700">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.contract_signed}
                    onChange={(e) => setFormData({ ...formData, contract_signed: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 text-brand-green focus:ring-brand-green"
                  />
                  <div>
                    <span className="text-white font-medium">{`${t('workspaces.contractSigned')}`}</span>
                    <p className="text-xs text-gray-500">Le client a signé l'autorisation de pentest</p>
                  </div>
                </label>
              </div>

              {/* Bouton d'envoi de contrat électronique */}
              <div className="bg-brand-black rounded-lg p-4 border border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-medium">{`${t('workspaces.sendContract')}`}</span>
                    <p className="text-xs text-gray-500 mt-1">Envoyez une demande de signature électronique au client</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!formData.contact_email) {
                        toast.error(t('common.error'));
                        return;
                      }
                      // TODO: Implémenter l'envoi de contrat électronique via API
                      toast.success(`Contrat envoyé à ${formData.contact_email}`);
                    }}
                    className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Envoyer
                  </button>
                </div>
              </div>

              {formData.contract_signed ? (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-medium">{`${t('workspaces.readyForTests')}`}</span>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    <span className="text-yellow-400 font-medium">{t('workspaces.contractNotSigned')}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>

        {/* Footer Actions */}
        <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-800">
          <div className="text-xs text-gray-500">
            {formData.company_name && <span>Client: {formData.company_name}</span>}
            {formData.vpn_name && <span className="ml-2">| VPN: {formData.vpn_name}</span>}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.name}
              className="btn-green px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sauvegarde...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Sauvegarder
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Schedule Modal Component
const CreateScheduleModal = ({ workspaceId, onClose, onCreated }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    name: '',
    scan_type: 'full',
    frequency: 'weekly',
    targets: '',
    time_of_day: '02:00',
    days_of_week: [0], // Monday
    auto_generate_report: true,
    report_format: 'pdf',
    send_notifications: true,
    notification_recipients: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.targets) {
      toast.error(t('common.error'));
      return;
    }

    try {
      setLoading(true);
      await schedulesApi.create({
        workspace_id: workspaceId,
        name: formData.name,
        scan_type: formData.scan_type,
        frequency: formData.frequency,
        targets: formData.targets.split(',').map(t => t.trim()).filter(Boolean),
        time_of_day: formData.time_of_day,
        days_of_week: formData.days_of_week,
        auto_generate_report: formData.auto_generate_report,
        report_format: formData.report_format,
        send_notifications: formData.send_notifications,
        notification_recipients: formData.notification_recipients.split(',').map(e => e.trim()).filter(Boolean)
      });
      toast.success(t('common.save'));
      onCreated();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  const days = [t('workspaces.dayMon'), t('workspaces.dayTue'), t('workspaces.dayWed'), t('workspaces.dayThu'), t('workspaces.dayFri'), t('workspaces.daySat'), t('workspaces.daySun')];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">{`${t('workspaces.createSchedule')}`}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">{t('workspaces.scheduleName')} *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
              placeholder={t('workspaces.scheduleNamePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">{t('workspaces.targetsCommaSeparated')}</label>
            <input
              type="text"
              value={formData.targets}
              onChange={(e) => setFormData({ ...formData, targets: e.target.value })}
              className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
              placeholder={t('workspaces.targetsPlaceholder')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('workspaces.scanType')}</label>
              <select
                value={formData.scan_type}
                onChange={(e) => setFormData({ ...formData, scan_type: e.target.value })}
                className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
              >
                <option value="quick">{t('workspaces.scanTypeQuick')}</option>
                <option value="full">{t('workspaces.scanTypeFull')}</option>
                <option value="vulnerability">{t('workspaces.scanTypeVulnerability')}</option>
                <option value="port_scan">{t('workspaces.scanTypePortScan')}</option>
                <option value="web_scan">{t('workspaces.scanTypeWebScan')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('workspaces.frequency')}</label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
              >
                <option value="daily">{t('workspaces.frequencyDaily')}</option>
                <option value="weekly">{t('workspaces.frequencyWeekly')}</option>
                <option value="biweekly">{t('workspaces.frequencyBiweekly')}</option>
                <option value="monthly">{t('workspaces.frequencyMonthly')}</option>
                <option value="quarterly">{t('workspaces.frequencyQuarterly')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">{t('workspaces.timeOfDay')}</label>
            <input
              type="time"
              value={formData.time_of_day}
              onChange={(e) => setFormData({ ...formData, time_of_day: e.target.value })}
              className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
            />
          </div>

          {formData.frequency === 'weekly' && (
            <div>
              <label className="block text-sm text-gray-400 mb-2">{t('workspaces.daysOfWeek')}</label>
              <div className="flex gap-2">
                {days.map((day, idx) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(idx)}
                    className={`px-3 py-1 rounded text-sm ${
                      formData.days_of_week.includes(idx)
                        ? 'bg-brand-green text-black'
                        : 'bg-brand-black border border-gray-700 text-gray-400'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-800">
            <div>
              <label className="flex items-center gap-3 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.auto_generate_report}
                  onChange={(e) => setFormData({ ...formData, auto_generate_report: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-600 text-brand-green focus:ring-brand-green"
                />
                {t('workspaces.autoGenerateReport')}
              </label>
            </div>
            <div>
              <label className="flex items-center gap-3 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.send_notifications}
                  onChange={(e) => setFormData({ ...formData, send_notifications: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-600 text-brand-green focus:ring-brand-green"
                />
                {t('workspaces.sendNotifications')}
              </label>
            </div>
          </div>

          {formData.send_notifications && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">{t('workspaces.notificationRecipients')}</label>
              <input
                type="text"
                value={formData.notification_recipients}
                onChange={(e) => setFormData({ ...formData, notification_recipients: e.target.value })}
                className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                placeholder="admin@exemple.fr, sécurité@exemple.fr"
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              {t('workspaces.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-green px-6 py-2 rounded-lg disabled:opacity-50"
            >
              {loading ? t('workspaces.creating') : t('workspaces.createSchedule')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Schedule Scan Wizard Component
const ScheduleScanWizard = ({ isOpen, onClose, initialDate, workspaces, selectedWorkspace, onScheduleCreated }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [scansLoading, setScansLoading] = useState(true);
  const [completedScans, setCompletedScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(selectedWorkspace?.id || '');
  const [scheduleData, setScheduleData] = useState({
    name: '',
    scheduled_date: initialDate ? new Date(initialDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    scheduled_time: '09:00',
    frequency: 'once',
    auto_report: true
  });

  // Load completed scans
  useEffect(() => {
    const loadScans = async () => {
      try {
        setScansLoading(true);
        // Fetch ALL scans (superadmin sees all)
        const response = await scansApi.list(0, 100, null);
        console.log('All scans response:', response);
        const allScans = response?.scans || [];

        // Filter to only show completed scans
        const completed = allScans.filter(scan => scan.status === 'completed');
        console.log('Completed scans:', completed.length);

        // If workspace is selected, filter by workspace
        const workspaceId = selectedWorkspaceId || selectedWorkspace?.id;
        if (workspaceId) {
          // Scans have workspaces array, not workspace_id
          const filtered = completed.filter(scan => {
            // Check if scan has workspaces array
            if (scan.workspaces && Array.isArray(scan.workspaces)) {
              return scan.workspaces.some(ws => ws.id === workspaceId);
            }
            // Fallback: check workspace_id directly
            return scan.workspace_id === workspaceId;
          });
          console.log('Filtered by workspace:', workspaceId, filtered.length);
          setCompletedScans(filtered);
        } else {
          setCompletedScans(completed);
        }
      } catch (err) {
        console.error('Error loading scans:', err);
        setCompletedScans([]);
      } finally {
        setScansLoading(false);
      }
    };
    if (isOpen) {
      loadScans();
    }
  }, [isOpen, selectedWorkspaceId, selectedWorkspace?.id]);

  // Update date when initialDate changes
  useEffect(() => {
    if (initialDate) {
      setScheduleData(prev => ({
        ...prev,
        scheduled_date: new Date(initialDate).toISOString().split('T')[0]
      }));
    }
  }, [initialDate]);

  const handleSubmit = async () => {
    if (!selectedScan && !scheduleData.name) {
      toast.error('Veuillez sélectionner un scan ou entrer un nom');
      return;
    }

    try {
      setLoading(true);
      const workspaceId = selectedWorkspaceId || selectedScan?.workspace_id || selectedWorkspace?.id;

      if (!workspaceId) {
        toast.error('Veuillez sélectionner un workspace');
        return;
      }

      // Parse targets - can be string (comma-separated) or already an array
      const targetValue = selectedScan?.target || '';
      const targetsList = Array.isArray(targetValue)
        ? targetValue
        : targetValue.split(',').map(t => t.trim()).filter(t => t);

      await schedulesApi.create({
        workspace_id: workspaceId,
        name: scheduleData.name || `Scan planifié - ${selectedScan?.name || 'Nouveau'}`,
        scan_type: selectedScan?.scan_type || 'full',
        targets: targetsList,
        frequency: scheduleData.frequency,
        start_date: `${scheduleData.scheduled_date}T${scheduleData.scheduled_time}:00`,
        time_of_day: scheduleData.scheduled_time,
        auto_generate_report: scheduleData.auto_report,
        scan_options: selectedScan?.config || {}
      });

      toast.success('Scan planifié avec succès');
      onScheduleCreated && onScheduleCreated();
    } catch (err) {
      console.error('Error creating schedule:', err);
      toast.error('Erreur lors de la planification du scan');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-brand-darker border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Calendar className="w-6 h-6 text-brand-green" />
                Planifier un scan
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                {step === 1 ? 'Sélectionnez un scan existant ou créez-en un nouveau' : 'Configurez la date et l\'heure'}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
              <XCircle className="w-6 h-6" />
            </button>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-2 mt-4">
            <div className={`flex-1 h-1 rounded ${step >= 1 ? 'bg-brand-green' : 'bg-gray-700'}`} />
            <div className={`flex-1 h-1 rounded ${step >= 2 ? 'bg-brand-green' : 'bg-gray-700'}`} />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {step === 1 ? (
            <div className="space-y-4">
              {/* Workspace Selection */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Workspace</label>
                <select
                  value={selectedWorkspaceId}
                  onChange={(e) => {
                    setSelectedWorkspaceId(e.target.value);
                    setSelectedScan(null);
                  }}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                >
                  <option value="">Tous les workspaces</option>
                  {workspaces.map(ws => (
                    <option key={ws.id} value={ws.id}>{ws.name}</option>
                  ))}
                </select>
              </div>

              {/* Completed Scans List */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Scans terminés ({completedScans.length})
                </label>
                {scansLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
                  </div>
                ) : completedScans.length === 0 ? (
                  <div className="text-center py-8 bg-brand-black/50 rounded-lg border border-gray-800">
                    <Target className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-400">Aucun scan terminé</p>
                    <p className="text-gray-500 text-sm">Lancez d'abord un scan pour le planifier</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[30vh] overflow-y-auto">
                    {completedScans.slice(0, 10).map(scan => (
                      <div
                        key={scan.id}
                        onClick={() => setSelectedScan(scan)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedScan?.id === scan.id
                            ? 'border-brand-green bg-brand-green/10'
                            : 'border-gray-700 hover:border-gray-600 bg-brand-black/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-white">{scan.name}</div>
                            <div className="text-sm text-gray-400 flex items-center gap-2 mt-1">
                              <Target className="w-3 h-3" />
                              {scan.target}
                              <span className="text-gray-600">|</span>
                              <span>{translateScanType(scan.scan_type)}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-400">
                              {new Date(scan.completed_at || scan.created_at).toLocaleDateString('fr-FR')}
                            </div>
                            {scan.vulnerabilities_count > 0 && (
                              <div className="text-xs text-orange-400">
                                {scan.vulnerabilities_count} vulns
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Or create new */}
              <div className="pt-4 border-t border-gray-800">
                <label className="block text-sm text-gray-400 mb-2">Ou créer un nouveau scan planifié</label>
                <input
                  type="text"
                  value={scheduleData.name}
                  onChange={(e) => setScheduleData({ ...scheduleData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  placeholder="Nom du scan planifié"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selected scan summary */}
              {selectedScan && (
                <div className="p-4 bg-brand-green/10 border border-brand-green/30 rounded-lg">
                  <div className="font-medium text-brand-green">{selectedScan.name}</div>
                  <div className="text-sm text-gray-400 mt-1">{selectedScan.target}</div>
                </div>
              )}

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Date</label>
                  <input
                    type="date"
                    value={scheduleData.scheduled_date}
                    onChange={(e) => setScheduleData({ ...scheduleData, scheduled_date: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Heure</label>
                  <input
                    type="time"
                    value={scheduleData.scheduled_time}
                    onChange={(e) => setScheduleData({ ...scheduleData, scheduled_time: e.target.value })}
                    className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                  />
                </div>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Fréquence</label>
                <select
                  value={scheduleData.frequency}
                  onChange={(e) => setScheduleData({ ...scheduleData, frequency: e.target.value })}
                  className="w-full px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
                >
                  <option value="once">Une seule fois</option>
                  <option value="daily">Quotidien</option>
                  <option value="weekly">Hebdomadaire</option>
                  <option value="monthly">Mensuel</option>
                </select>
              </div>

              {/* Auto report */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="auto_report"
                  checked={scheduleData.auto_report}
                  onChange={(e) => setScheduleData({ ...scheduleData, auto_report: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-600 text-brand-green focus:ring-brand-green"
                />
                <label htmlFor="auto_report" className="text-sm text-gray-400 cursor-pointer">
                  Générer automatiquement un rapport après le scan
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 flex justify-between">
          <button
            onClick={() => step === 1 ? onClose() : setStep(1)}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            {step === 1 ? 'Annuler' : 'Retour'}
          </button>
          <button
            onClick={() => step === 1 ? setStep(2) : handleSubmit()}
            disabled={loading || (step === 1 && !selectedScan && !scheduleData.name)}
            className="btn-green px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Planification...
              </>
            ) : step === 1 ? (
              <>
                Suivant
                <ChevronRight className="w-4 h-4" />
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4" />
                Planifier
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceManager;
